
import { Info } from "lucide-react";
import { motion } from "framer-motion";

interface LoanCalculationSummaryProps {
  monthlyPayment: number;
  totalPaid: number;
  interestAndFees: number;
  interestRate: number;
  monthlyFee?: number;
  setupFee?: number;
  showAsEstimate?: boolean;
  language?: "fi" | "sv" | "en" | "he" | "ar";
}

export const LoanCalculationSummary = ({
  monthlyPayment,
  totalPaid,
  interestAndFees,
  interestRate,
  monthlyFee = 5,
  setupFee = 150,
  showAsEstimate = false,
  language = "fi"
}: LoanCalculationSummaryProps) => {
  // Translations and labels based on selected language
  const translations = {
    fi: {
      estimate: "Arvio lainan kustannuksista",
      monthlyPayment: "Kuukausierä",
      interestRate: "Korko",
      monthlyFee: "Kuukausimaksu",
      setupFee: "Perustamismaksu",
      totalPaid: "Yhteensä maksettavaa",
      interestAndFees: "Korot ja kulut yhteensä",
    },
    en: {
      estimate: "Loan Cost Estimate",
      monthlyPayment: "Monthly Payment",
      interestRate: "Interest Rate",
      monthlyFee: "Monthly Fee",
      setupFee: "Setup Fee",
      totalPaid: "Total to Pay",
      interestAndFees: "Total Interest & Fees",
    },
    sv: {
      estimate: "Uppskattning av lånekostnad",
      monthlyPayment: "Månadsbetalning",
      interestRate: "Ränta",
      monthlyFee: "Månadsavgift",
      setupFee: "Startavgift",
      totalPaid: "Totalt att betala",
      interestAndFees: "Totala räntor och avgifter",
    },
    he: {
      estimate: "אומדן עלות ההלוואה",
      monthlyPayment: "תשלום חודשי",
      interestRate: "ריבית",
      monthlyFee: "עמלה חודשית",
      setupFee: "עמלת הקמה",
      totalPaid: "סך הכל לתשלום",
      interestAndFees: "סך הריביות והעמלות",
    },
    ar: {
      estimate: "تقدير تكلفة القرض",
      monthlyPayment: "دفعة شهرية",
      interestRate: "سعر الفائدة",
      monthlyFee: "رسوم شهرية",
      setupFee: "رسوم إعداد",
      totalPaid: "المبلغ الإجمالي للدفع",
      interestAndFees: "إجمالي الفوائد والرسوم",
    }
  };

  const text = translations[language];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white p-3 sm:p-4 rounded-xl border border-blue-100 shadow-sm"
    >
      <div className="flex items-center mb-3">
        <h3 className="text-sm sm:text-base font-bold text-gray-800 flex items-center flex-wrap">
          <span className="mr-1">{text.estimate}</span>
          {showAsEstimate && (
            <span className="inline-flex items-center ml-1 text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              <Info className="h-3 w-3 mr-1" />
              <span>Alustava</span>
            </span>
          )}
        </h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 mb-4">
        <motion.div 
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100/70 p-2 sm:p-3 rounded-lg border border-blue-100/80"
        >
          <div className="text-xs sm:text-sm text-gray-600">{text.monthlyPayment}</div>
          <div className="text-sm sm:text-lg font-bold text-blue-700">{monthlyPayment.toFixed(2)} €</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100/70 p-2 sm:p-3 rounded-lg border border-blue-100/80"
        >
          <div className="text-xs sm:text-sm text-gray-600">{text.interestRate}</div>
          <div className="text-sm sm:text-lg font-bold text-blue-700">{interestRate.toFixed(2)} %</div>
        </motion.div>
        
        <motion.div 
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100/70 p-2 sm:p-3 rounded-lg border border-blue-100/80"
        >
          <div className="text-xs sm:text-sm text-gray-600">{text.setupFee}</div>
          <div className="text-sm sm:text-lg font-bold text-blue-700">{setupFee.toFixed(2)} €</div>
        </motion.div>
      </div>

      <div className="space-y-2 border-t border-gray-100 pt-3">
        <div className="flex justify-between items-center">
          <span className="text-xs sm:text-sm text-gray-600 w-[60%] pr-1">{text.totalPaid}</span>
          <span className="font-semibold text-right text-sm sm:text-base text-blue-700 w-[40%]">{totalPaid.toFixed(2)} €</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs sm:text-sm text-gray-600 w-[60%] pr-1">{text.interestAndFees}</span>
          <span className="font-semibold text-right text-sm sm:text-base text-blue-700 w-[40%]">{interestAndFees.toFixed(2)} €</span>
        </div>
      </div>
    </motion.div>
  );
};
